import { parseGapTemplate, type GapSegment } from './gapTemplate';

/**
 * «Дополни таблицу» — тип `table_fill` (решение владельца 13.10.2026; договор
 * docs/CONTRACTS/block-types-1.1.2.md §3).
 *
 * Ячейка — маленький шаблон в синтаксисе `gap_fill`: `[[went]]` во всю ячейку —
 * пропуск-ячейка; `She [[goes|is going]] to school.` — пропуск внутри предложения
 * (их в ячейке может быть несколько). Строка заголовка пропусков не держит.
 *
 * ⚠️ ПОРЯДОК ПРОПУСКОВ — СЛЕВА НАПРАВО, СВЕРХУ ВНИЗ, БЕЗ СТРОКИ ЗАГОЛОВКА. В нём
 * уходит `answer_data` (массив строк, как у `gap_fill`), и в нём же сервер склеивает
 * ячейки в один шаблон (progress-service block_grader.go, tableTemplate) — разойдись
 * порядок, проверка сервера поставит ученику чужие ответы не в те пропуски.
 */

export type TableFillContent = {
  instruction?: string;
  header?: boolean;
  rows?: unknown;
  mode?: 'input' | 'drag';
  distractors?: string[];
  case_sensitive?: boolean;
};

export const TABLE_MAX_COLS = 8;
export const TABLE_MAX_ROWS = 40;

type GapDef = Extract<GapSegment, { kind: 'gap' }>;

export type TableCell = {
  text: string;
  /** Разбор ячейки; у заголовка — только текст. */
  segments: GapSegment[];
  /** Номер первого пропуска ячейки в общем порядке (−1 — пропусков нет). */
  gapStart: number;
  /** Вся ячейка — один пропуск: у ученика поле во всю ширину. */
  wholeGap: boolean;
};

export type TableModel = {
  header: boolean;
  cols: number;
  /** Строка заголовка (если есть) — первая. */
  rows: TableCell[][];
  gaps: GapDef[];
};

/** Прямоугольная таблица строк: недостающие ячейки — пустые, лишнее не ломает. */
export function tableRows(c: TableFillContent | null | undefined): string[][] {
  const raw = Array.isArray(c?.rows) ? (c!.rows as unknown[]) : [];
  const rows = raw.map((r) => (Array.isArray(r) ? r.map((x) => (typeof x === 'string' ? x : '')) : []));
  const cols = Math.max(0, ...rows.map((r) => r.length));
  return rows.map((r) => [...r, ...Array(cols - r.length).fill('')]);
}

export function tableModel(c: TableFillContent | null | undefined): TableModel {
  const header = c?.header === true;
  const rows = tableRows(c);
  const gaps: GapDef[] = [];
  const out = rows.map((r, ri) =>
    r.map((text): TableCell => {
      if (header && ri === 0) return { text, segments: [{ kind: 'text', text }], gapStart: -1, wholeGap: false };
      const segments = parseGapTemplate(text);
      const own = segments.filter((s): s is GapDef => s.kind === 'gap');
      const gapStart = own.length ? gaps.length : -1;
      gaps.push(...own);
      const wholeGap = segments.length === 1 && segments[0].kind === 'gap';
      return { text, segments, gapStart, wholeGap };
    }),
  );
  return { header, cols: rows[0]?.length ?? 0, rows: out, gaps };
}

/** Есть ли что пройти: хотя бы один пропуск с ответом (правило «считать только проходимое»). */
export const tableHasGaps = (c: TableFillContent | null | undefined): boolean =>
  tableModel(c).gaps.some((g) => g.answers.some(Boolean));

/**
 * Раскладка у ученика (решение владельца 13.10.2026 по кадрам 390):
 * от 640 px — таблица; уже — карточками, если есть заголовок и столбцов не меньше
 * трёх (подписи берутся из заголовка), иначе таблица с прокруткой вбок и
 * закреплённым первым столбцом.
 */
export function tableLayout(header: boolean, cols: number, narrow: boolean): 'table' | 'cards' | 'scroll' {
  if (!narrow) return 'table';
  return header && cols >= 3 ? 'cards' : 'scroll';
}
